import React, { useMemo, useState } from 'react';
import { Store, Warehouse, Building2, MapPin, Search, Plus, Pencil, Archive, ArchiveRestore, CalendarDays } from 'lucide-react';
import type { Sucursal } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, inputStyle, EmptyState, StatCard, MotionGrid, MotionItem, useIsMobile, usePagination, Pagination, T } from '../ui';

// Identidad visual por tipo de punto. El color viaja al ícono y a una franja
// lateral fina; no se tiñe la tarjeta entera para que el nombre siga siendo
// lo que más pesa a la vista.
const TIPOS: Record<string, {
  icono: React.ElementType;
  color: string;
  soft: string;
}> = {
  tienda: {
    icono: Store,
    color: '#FF6B01',
    soft: 'rgba(255,107,1,.12)'
  },
  bodega: {
    icono: Warehouse,
    color: '#2563eb',
    soft: 'rgba(37,99,235,.12)'
  },
  oficina: {
    icono: Building2,
    color: '#7c3aed',
    soft: 'rgba(124,58,237,.12)'
  }
};
const TIPO_POR_DEFECTO = {
  icono: MapPin,
  color: '#16a34a',
  soft: 'rgba(22,163,74,.12)'
};
function estiloTipo(tipo: string) {
  return TIPOS[(tipo ?? '').toLowerCase().trim()] ?? TIPO_POR_DEFECTO;
}
function fmtFecha(ms: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}
export function Sucursales({
  sucs,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: {
  sucs: Sucursal[];
  onNew?: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  canArchive?: boolean;
}) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [busqueda, setBusqueda] = useState('');
  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return sucs.filter(s => tab === 'activos' ? !s.archivado : !!s.archivado).filter(s => !q || s.nombre.toLowerCase().includes(q) || (s.direccion ?? '').toLowerCase().includes(q) || (s.tipo ?? '').toLowerCase().includes(q)).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [sucs, tab, busqueda]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtradas, 9);
  const stats = useMemo(() => {
    const activas = sucs.filter(s => !s.archivado);
    return {
      activas: activas.length,
      tipos: new Set(activas.map(s => (s.tipo ?? '').toLowerCase().trim()).filter(Boolean)).size,
      archivadas: sucs.filter(s => s.archivado).length
    };
  }, [sucs]);
  const pestanas: {
    id: 'activos' | 'archivados';
    label: string;
    conteo: number;
  }[] = [{
    id: 'activos',
    label: 'Activas',
    conteo: stats.activas
  }, {
    id: 'archivados',
    label: 'Archivadas',
    conteo: stats.archivadas
  }];
  return <PageShell title="Sucursales" sub="Puntos comerciales registrados en el sistema" action={onNew ? <button onClick={onNew} style={primaryButton} data-id="element-1150" data-mp="s59di6ry3a19">
            <Plus size={15} data-id="element-1151" data-mp="2cqfpxaecf0l2" /> Nueva sucursal
          </button> : undefined} data-id="element-1149" data-mp="n6682l81z1sr">

      <style data-id="element-1152" data-mp="27ps2szt8j3w4">{`
        .suc-card { position: relative; overflow: hidden; transition: transform .16s, box-shadow .16s; }
        .suc-card:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(0,0,0,.09); }
        /* Franja del color del tipo, al costado izquierdo. */
        .suc-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--tono); }
      `}</style>

      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0,1fr))',
      gap: 14,
      marginBottom: 22
    }} data-id="element-1153" data-mp="2rfc0be3vgpou">

        <StatCard label="Activas" value={String(stats.activas)} note="en operación" data-id="element-1154" data-mp="2hopm9obt0789" />

        <StatCard label="Tipos distintos" value={String(stats.tipos)} note="tienda, bodega, oficina…" accent={T.info} data-id="element-1155" data-mp="3f3v4pijswibn" />

        <StatCard label="Archivadas" value={String(stats.archivadas)} note="fuera del listado" accent={T.warn} data-id="element-1156" data-mp="3aclg8l9znl1c" />

      </div>

      {/* ── BUSCADOR Y PESTAÑAS ── */}
      <div style={{
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      alignItems: 'center',
      marginBottom: 20
    }} data-id="element-1157" data-mp="2tzyu3xo0palg">

        <div style={{
        position: 'relative',
        flex: '1 1 240px'
      }} data-id="element-1158" data-mp="2kxplxeg81tb8">
          <Search size={15} style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: T.textMuted
        }} data-id="element-1159" data-mp="2pviqk31vmf9e" />

          <input style={{
          ...inputStyle,
          paddingLeft: 34
        }} placeholder="Buscar por nombre, dirección o tipo" value={busqueda} onChange={e => setBusqueda(e.target.value)} data-id="element-1160" data-mp="3hnq5mz1qhjlo" />

        </div>

        <div style={{
        display: 'flex',
        gap: 7
      }} data-id="element-1161" data-mp="38u3ghrs17elg">
          {pestanas.map(p => {
          const activa = tab === p.id;
          return <button key={p.id} type="button" onClick={() => setTab(p.id)} style={{
            padding: '9px 15px',
            borderRadius: 99,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            whiteSpace: 'nowrap',
            color: activa ? T.accent : T.textSecondary,
            background: activa ? T.accentSoft : 'transparent',
            border: `1px solid ${activa ? T.accentBorder : T.border}`,
            transition: 'all .15s'
          }} data-id="element-1162" data-mp="d0ahc4wy39d">

                {p.label}
                <span style={{
              fontSize: 11,
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 99,
              background: activa ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
              color: activa ? T.accent : T.textMuted
            }} data-id="element-1163" data-mp="3nqv3a7yo5hrb">

                  {p.conteo}
                </span>
              </button>;
        })}
        </div>
      </div>

      {/* ── LISTADO ── */}
      {filtradas.length === 0 ? <EmptyState title={busqueda ? 'Ninguna sucursal coincide' : tab === 'activos' ? 'Sin sucursales activas' : 'Sin sucursales archivadas'} hint={busqueda ? 'Prueba con otro texto o revisa la otra pestaña.' : tab === 'activos' ? 'Registra el primer punto comercial para empezar.' : 'Las sucursales que archives aparecerán aquí.'} icon={<MapPin size={22} data-id="element-1165" data-mp="1xz56v7emeejc" />} data-id="element-1164" data-mp="1k0ypr2xw4t6d" /> : <>
          <MotionGrid style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))',
        gap: 14
      }} data-id="element-1166" data-mp="1xy3v10ps5o0k">

            {pageItems.map(s => {
          const tipo = estiloTipo(s.tipo);
          const Icono = tipo.icono;
          return <MotionItem key={s.id} className="suc-card" style={{
            ...glassLight,
            padding: 0,
            ['--tono' as string]: tipo.color
          }} data-id="element-1167" data-mp="1a9avku9y8y17">

                  <div style={{
              padding: '18px 20px 16px'
            }} data-id="element-1168" data-mp="hl6oq5fn6ewx">
                    {/* Encabezado: ícono y nombre. Los botones van abajo, en su
                     propia fila, para que nunca aplasten el título. */}
                    <div style={{
                display: 'flex',
                gap: 13,
                alignItems: 'flex-start',
                marginBottom: 14
              }} data-id="element-1169" data-mp="ezpe9q6772lk">

                      <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: 13,
                  flexShrink: 0,
                  background: tipo.soft,
                  border: `1px solid ${tipo.color}30`,
                  color: tipo.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }} data-id="element-1170" data-mp="51rjejtu27jz">

                        <Icono size={19} data-id="element-1171" data-mp="3djnxc3rncefs" />
                      </div>

                      <div style={{
                  minWidth: 0,
                  flex: 1
                }} data-id="element-1172" data-mp="3dhb8r7rdv5tr">
                        <div style={{
                    fontSize: 15.5,
                    fontWeight: 800,
                    color: T.text,
                    lineHeight: 1.3,
                    wordBreak: 'break-word'
                  }} data-id="element-1173" data-mp="34ips5i5h20vm">

                          {s.nombre}
                        </div>
                        <span style={{
                    display: 'inline-block',
                    marginTop: 6,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    padding: '3px 9px',
                    borderRadius: 99,
                    color: tipo.color,
                    background: tipo.soft,
                    border: `1px solid ${tipo.color}30`
                  }} data-id="element-1174" data-mp="mc3rm0b30syt">

                          {s.tipo || 'Sin tipo'}
                        </span>
                      </div>
                    </div>

                    {/* Datos */}
                    <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontSize: 12.5,
                color: T.textSecondary
              }} data-id="element-1175" data-mp="3urngyo8mtr8l">

                      <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start'
                }} data-id="element-1176" data-mp="3751sskrzqf75">

                        <MapPin size={14} style={{
                    color: T.textMuted,
                    flexShrink: 0,
                    marginTop: 2
                  }} data-id="element-1177" data-mp="3l1oqwqvojben" />

                        <span style={{
                    lineHeight: 1.45,
                    wordBreak: 'break-word'
                  }} data-id="element-1178" data-mp="3v0e62bw6yhr7">

                          {s.direccion || 'Sin dirección registrada'}
                        </span>
                      </div>
                      <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center'
                }} data-id="element-1179" data-mp="3oup4e9kshyd">

                        <CalendarDays size={14} style={{
                    color: T.textMuted,
                    flexShrink: 0
                  }} data-id="element-1180" data-mp="cohqx167vpry" />

                        <span data-id="element-1181" data-mp="30wv7q91iaj12">Registrada el {fmtFecha(s.creado)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Acciones, separadas del contenido por una línea */}
                  {onEdit || onDelete && canArchive ? <div style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              padding: '11px 20px',
              borderTop: `1px solid ${T.border}`,
              background: T.surface
            }} data-id="element-1182" data-mp="2n4fchueujpap">

                      {onEdit ? <button type="button" onClick={() => onEdit(s.id)} style={{
                ...ghostButton,
                padding: '7px 13px',
                fontSize: 12.5
              }} data-id="element-1183" data-mp="3athqmsa9v00g">

                          <Pencil size={13} data-id="element-1184" data-mp="1ze1ivzgm5ie0" /> Editar
                        </button> : null}
                      {onDelete && canArchive ? <button type="button" onClick={() => onDelete(s.id)} style={{
                ...ghostButton,
                padding: '7px 13px',
                fontSize: 12.5,
                color: s.archivado ? T.info : T.warn,
                borderColor: s.archivado ? `${T.info}44` : `${T.warn}44`,
                background: s.archivado ? T.infoSoft : T.warnSoft
              }} data-id="element-1185" data-mp="1ueou3966m18s">

                          {s.archivado ? <>
                              <ArchiveRestore size={13} data-id="element-1186" data-mp="1z1ik2tgtpngu" /> Desarchivar
                            </> : <>
                              <Archive size={13} data-id="element-1187" data-mp="233vl54kmkx7a" /> Archivar
                            </>}
                        </button> : null}
                    </div> : null}
                </MotionItem>;
        })}
          </MotionGrid>

          <div style={{
        marginTop: 18
      }} data-id="element-1188" data-mp="2hz1r5rjq0w0m">
            <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="sucursales" data-id="element-1189" data-mp="2hyjoac2jzjjq" />

          </div>
        </>}
    </PageShell>;
}